import { Archive, Lock, Plus, RotateCcw } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  NO_RECENT_USE,
  REASON_LISTS,
  REASON_RECENT_DAYS,
  REJECTION_STAGE_LABELS,
  type AdminReasonRow,
  type ReasonList,
  type ReasonRecentUse,
  type RejectionStage,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { ModuleGate } from '../../../components/ModuleGate';
import { Segmented } from '../../../components/Segmented';
import { SkeletonLines } from '../../../components/Skeleton';
import { Switch } from '../../../components/Switch';
import { ARCHIVED_TONE } from '../../../components/status-tones';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { ToneChip } from '../../../components/Tone';
import { errorMessage } from '../../../lib/api-client';
import { formatNumber } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { useCanEdit, useCanView } from '../../../stores/session';
import { REASON_ROWS, reasonsApi, useReasons } from '../api/reasons-api';
import { ListTable, type ListColumn, type ListGroup } from '../components/simple-list/ListTable';
import { FilterPills, ListNoMatch, ListSearch, ListTabs, ListToolbar, PanelActions, PanelFacts, PanelSection, SidePanel, SimpleListPage, usePanelMode } from '../components/simple-list/SimpleList';
import { oneOf, useListAddress } from '../components/simple-list/use-list-address';
import { isPendingRow, pendingId, useRowChanges, type RowChanges } from '../components/simple-list/use-row-changes';

const PATH = '/admin/reasons';
const STAGE_ORDER: readonly RejectionStage[] = ['screening', 'assessment', 'interview', 'any'];
const FILTERS = ['in_use', 'retired', 'added'] as const;
type ReasonFilter = (typeof FILTERS)[number];
/** The panel's "add" mode, kept in the address like a picked row. */
const ADDING = 'new';
const LIST_TITLES: Readonly<Record<ReasonList, string>> = { rejection: 'Rejection reasons', withdrawal: 'Withdrawal reasons' };

/**
 * ADMIN-07 / F19.07 — the §L19 rejection and §L20 withdrawal reasons (DECISIONS-LOG S15 Q5), in the simple-list pattern
 * (Admin redesign batch 2, option A): two tabs, the rejection reasons banded by stage, filter pills and search, and the
 * picked reason in the panel to rename, switch the pool prompt, or retire — at once, with a 10-second Undo. A retired
 * reason leaves every picker and stays on every past rejection; the few the system relies on are renamed, never retired.
 * Admin and HR (`hiring_setup`).
 */
export function ReasonsPage(): ReactNode {
  const canView = useCanView('hiring_setup');
  const canEdit = useCanEdit('hiring_setup');
  const isPoolsOn = useModule('pools');
  const reasons = useReasons(canView);
  const changes = useRowChanges(REASON_ROWS);
  const mode = usePanelMode();
  const [address, setAddress] = useListAddress(PATH);
  const tab = oneOf(address.tab, REASON_LISTS, 'rejection');
  const filter = FILTERS.find((value) => value === address.show) ?? null;
  const query = address.q ?? '';

  if (!canView) return <ForbiddenState resource="the reason lists" contact="Your admin" />;
  if (reasons.isPending) return <SkeletonLines count={10} />;
  if (reasons.error && !reasons.data) return <ServerErrorState onRetry={() => void reasons.refetch()} />;

  const lists = reasons.data;
  const all = lists[tab];
  const shown = all.filter((row) => matchesFilter(row, filter) && matchesQuery(row, query));
  const open = address.open === ADDING ? null : (all.find((row) => row.id === address.open) ?? null);
  const isAdding = address.open === ADDING && canEdit;
  const close = (): void => setAddress({ open: undefined });

  const columns: ListColumn<AdminReasonRow>[] = [
    { key: 'code', header: 'Code', isWideOnly: true, render: (row) => <code>{isPendingRow(row.id) ? '—' : row.code}</code> },
    { key: 'used', header: 'Used', isNumeric: true, isWideOnly: true, render: (row) => usedText(row.timesUsed) },
    ...(isPoolsOn ? [{ key: 'pool', header: 'Talent pool', isWideOnly: true, render: (row: AdminReasonRow) => (row.triggersPool ? 'Offered' : '—') }] : []),
  ];

  return (
    <SimpleListPage
      crumb="Admin · Hiring setup"
      title="Reasons"
      lede="Why candidates are rejected or withdraw. Every rejection needs one, which is where every rejection report comes from; candidates never see them. Rename, add or retire — a retired reason leaves every picker and stays on every past rejection."
      actions={
        canEdit && (
          <Button variant="solid" icon={Plus} onClick={() => setAddress({ open: ADDING })}>
            Add a reason
          </Button>
        )
      }
      tabs={
        <ListTabs
          label="Which list"
          value={tab}
          options={REASON_LISTS.map((list) => ({ value: list, label: LIST_TITLES[list], count: lists[list].length }))}
          onChange={(next) => setAddress({ tab: next === 'rejection' ? undefined : next, open: isAdding ? ADDING : undefined })}
        />
      }
      toolbar={
        <ListToolbar search={<ListSearch label="Find a reason" value={query} onChange={(next) => setAddress({ q: next })} />}>
          <FilterPills
            label="Show reasons that are"
            value={filter}
            options={[
              { value: 'in_use', label: 'In use', count: all.filter((row) => row.retiredAt === null).length },
              { value: 'retired', label: 'Retired', count: all.filter((row) => row.retiredAt !== null).length },
              { value: 'added', label: 'Added', count: all.filter((row) => row.isCustom).length },
            ]}
            onChange={(next) => setAddress({ show: next ?? undefined })}
          />
        </ListToolbar>
      }
      panel={
        isAdding ? (
          <SidePanel mode={mode} label={`Add a ${tab} reason`} title={tab === 'rejection' ? 'Add a rejection reason' : 'Add a withdrawal reason'} onClose={close}>
            <AddReason list={tab} changes={changes} onAdded={(id) => setAddress({ open: id, show: undefined, q: undefined })} />
          </SidePanel>
        ) : (
          open && (
            <SidePanel mode={mode} label={`Reason: ${open.label}`} title={open.label} sub={<ReasonChips row={open} />} onClose={close}>
              <ReasonPanel key={open.id} row={open} canEdit={canEdit} changes={changes} />
            </SidePanel>
          )
        )
      }
    >
      <ListTable
        label={LIST_TITLES[tab]}
        name={{ header: 'Reason', label: (row) => row.label, render: (row) => <ReasonName row={row} /> }}
        columns={columns}
        groups={groupsOf(tab, shown)}
        idOf={(row) => row.id}
        selectedId={open?.id ?? null}
        onOpen={(row) => setAddress({ open: row.id })}
        isMuted={(row) => row.retiredAt !== null}
        isPending={(row) => isPendingRow(row.id)}
        phoneLine={(row) => `${isPendingRow(row.id) ? 'Saving…' : row.code} · ${usedText(row.timesUsed)}`}
        trailing={(row) =>
          canEdit &&
          row.retiredAt !== null && (
            <Button size="sm" variant="ghost" icon={RotateCcw} onClick={() => bringBack(changes, row)}>
              Bring back
            </Button>
          )
        }
        empty={<ListNoMatch what="reason" query={query} onClear={() => setAddress({ q: undefined, show: undefined })} />}
      />
    </SimpleListPage>
  );
}

function ReasonName({ row }: { readonly row: AdminReasonRow }): ReactNode {
  return (
    <>
      {row.label}
      {row.isLocked && <Lock size={12} aria-label="The system relies on this reason, so it can't be retired" />}
      {row.isCustom && <Chip plain>Added</Chip>}
      {row.retiredAt !== null && <ToneChip tone={ARCHIVED_TONE}>Retired</ToneChip>}
    </>
  );
}

function ReasonChips({ row }: { readonly row: AdminReasonRow }): ReactNode {
  return (
    <>
      {row.isLocked && <Chip plain>The system relies on it</Chip>}
      {row.isCustom && <Chip plain>Added</Chip>}
      {row.retiredAt !== null && <ToneChip tone={ARCHIVED_TONE}>Retired</ToneChip>}
    </>
  );
}

function ReasonPanel({ row, canEdit, changes }: { readonly row: AdminReasonRow; readonly canEdit: boolean; readonly changes: RowChanges<AdminReasonRow> }): ReactNode {
  // Untouched, the box shows the saved name, so someone else's rename (or an Undo) shows as it lands.
  const [draft, setDraft] = useState<string | null>(null);
  const name = draft ?? row.label;
  const isRetired = row.retiredAt !== null;
  const trimmed = name.trim();
  const isNameValid = trimmed.length >= 3 && trimmed.length <= 80;
  const isChanged = trimmed !== row.label;

  const rename = (): void => {
    if (!isNameValid || !isChanged) return;
    const before = row.label;
    setDraft(null);
    changes.changeWithUndo(
      { id: row.id, patch: { label: trimmed }, send: reasonsApi.rename(row.id, trimmed), failure: "The reason wasn't renamed" },
      { message: `Renamed to “${trimmed}”`, undo: { id: row.id, patch: { label: before }, send: reasonsApi.rename(row.id, before), failure: "The old name wasn't put back" } },
    );
  };

  return (
    <>
      <PanelSection>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            rename();
          }}
        >
          <Field label="Name" hint="Recruiters pick it by this name. The code stays the same." error={trimmed.length > 0 && trimmed.length < 3 ? 'At least 3 characters.' : undefined}>
            {({ inputId, describedBy, isInvalid }) => (
              <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} value={name} maxLength={80} disabled={!canEdit || isRetired} onChange={(event) => setDraft(event.target.value)} />
            )}
          </Field>
        </form>
        <PanelFacts
          facts={[
            ...(row.stage !== null ? [{ label: 'Stage', value: REJECTION_STAGE_LABELS[row.stage] }] : []),
            { label: 'Code', value: <code>{row.code}</code> },
            { label: 'Used', value: row.timesUsed === 0 ? 'Never' : `${formatNumber(row.timesUsed)} ${row.timesUsed === 1 ? 'time' : 'times'}` },
          ]}
        />
      </PanelSection>
      <PanelSection title={`Last ${REASON_RECENT_DAYS} days`}>
        <RecentUse recent={row.lastThirtyDays} />
      </PanelSection>
      <ModuleGate module="pools">
        <PanelSection title="Talent pool">
          <Switch
            checked={row.triggersPool}
            disabled={!canEdit || isRetired}
            label="Offer the talent pool when it is picked"
            onChange={(checked) =>
              changes.changeWithUndo(
                { id: row.id, patch: { triggersPool: checked }, send: reasonsApi.setPoolPrompt(row.id, checked), failure: "The pool prompt wasn't changed" },
                { message: checked ? 'Pool prompt switched on' : 'Pool prompt switched off', undo: { id: row.id, patch: { triggersPool: !checked }, send: reasonsApi.setPoolPrompt(row.id, !checked), failure: "The pool prompt wasn't put back" } },
              )
            }
          />
        </PanelSection>
      </ModuleGate>
      {canEdit && (
        <PanelSection>
          {row.isLocked && !isRetired && <p>The system relies on this reason, so it can be renamed but never retired.</p>}
          <PanelActions>
            {isRetired ? (
              <Button variant="outline" size="sm" icon={RotateCcw} onClick={() => bringBack(changes, row)}>
                Bring back
              </Button>
            ) : (
              !row.isLocked && (
                <Button variant="ghost" size="sm" icon={Archive} onClick={() => retire(changes, row)}>
                  Retire
                </Button>
              )
            )}
            <Button variant="solid" size="sm" disabled={isRetired || !isNameValid || !isChanged} onClick={rename}>
              Save name
            </Button>
          </PanelActions>
        </PanelSection>
      )}
    </>
  );
}

/** "Senior accountant 6 · Sales executive 4 · Backend engineer 2 · other jobs 6". */
function RecentUse({ recent }: { readonly recent: ReasonRecentUse }): ReactNode {
  if (recent.total === 0) return <p>Not picked in the last {REASON_RECENT_DAYS} days.</p>;
  const parts = [...recent.jobs.map((job) => `${job.title} ${formatNumber(job.count)}`), ...(recent.otherJobs > 0 ? [`other jobs ${formatNumber(recent.otherJobs)}`] : [])];
  return (
    <p>
      <strong>{formatNumber(recent.total)}</strong> {recent.total === 1 ? 'time' : 'times'}: {parts.join(' · ')}
    </p>
  );
}

function AddReason({ list, changes, onAdded }: { readonly list: ReasonList; readonly changes: RowChanges<AdminReasonRow>; readonly onAdded: (id: string) => void }): ReactNode {
  const [label, setLabel] = useState('');
  const [stage, setStage] = useState<RejectionStage>('screening');
  const [triggersPool, setTriggersPool] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = label.trim();

  const add = async (): Promise<void> => {
    if (trimmed.length < 3) {
      setError('Give the reason a label of at least 3 characters.');
      return;
    }
    const typed = { label: trimmed, stage, triggersPool };
    setError(null);
    setLabel('');
    try {
      const saved = await changes.add(placeholderOf(list, typed), () => reasonsApi.create({ list, label: typed.label, triggersPool: typed.triggersPool, stage: list === 'rejection' ? typed.stage : null }));
      onAdded(saved.id);
    } catch (cause) {
      // A refusal keeps what was typed (review W20).
      setLabel(typed.label);
      setError(errorMessage(cause));
    }
  };

  return (
    <PanelSection>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void add();
        }}
      >
        <Field label="Name" error={error ?? undefined} hint="It gets its own code, which never changes.">
          {({ inputId, describedBy, isInvalid }) => (
            <Input id={inputId} autoFocus aria-describedby={describedBy} isInvalid={isInvalid} value={label} maxLength={80} placeholder="e.g. Not willing to work night shift" onChange={(event) => setLabel(event.target.value)} />
          )}
        </Field>
      </form>
      {list === 'rejection' && <Segmented label="Stage" value={stage} options={STAGE_ORDER.map((value) => ({ value, label: REJECTION_STAGE_LABELS[value] }))} onChange={setStage} />}
      <ModuleGate module="pools">
        <Switch checked={triggersPool} onChange={setTriggersPool} label="Offer the talent pool when it is picked" />
      </ModuleGate>
      <PanelActions>
        <span />
        <Button variant="solid" size="sm" icon={Plus} disabled={trimmed.length < 3} onClick={() => void add()}>
          Add
        </Button>
      </PanelActions>
    </PanelSection>
  );
}

function retire(changes: RowChanges<AdminReasonRow>, row: AdminReasonRow): void {
  changes.changeWithUndo(
    { id: row.id, patch: { retiredAt: new Date().toISOString() }, send: reasonsApi.retire(row.id), failure: "The reason wasn't retired" },
    { message: `“${row.label}” retired`, undo: { id: row.id, patch: { retiredAt: null }, send: reasonsApi.restore(row.id), failure: "The reason wasn't brought back" } },
  );
}

function bringBack(changes: RowChanges<AdminReasonRow>, row: AdminReasonRow): void {
  changes.changeWithUndo(
    { id: row.id, patch: { retiredAt: null }, send: reasonsApi.restore(row.id), failure: "The reason wasn't brought back" },
    { message: `“${row.label}” is back in the lists`, undo: { id: row.id, patch: { retiredAt: row.retiredAt }, send: reasonsApi.retire(row.id), failure: "The reason wasn't retired again" } },
  );
}

function placeholderOf(list: ReasonList, typed: { readonly label: string; readonly stage: RejectionStage; readonly triggersPool: boolean }): AdminReasonRow {
  return {
    id: pendingId(),
    list,
    code: '',
    label: typed.label,
    stage: list === 'rejection' ? typed.stage : null,
    triggersPool: typed.triggersPool,
    isLocked: false,
    isCustom: true,
    retiredAt: null,
    timesUsed: 0,
    lastThirtyDays: NO_RECENT_USE,
    version: 0,
  };
}

function groupsOf(list: ReasonList, rows: readonly AdminReasonRow[]): readonly ListGroup<AdminReasonRow>[] {
  if (list === 'withdrawal') return [{ key: 'all', rows }];
  return STAGE_ORDER.map((stage) => ({ key: stage, label: REJECTION_STAGE_LABELS[stage], rows: rows.filter((row) => row.stage === stage) }));
}

function matchesFilter(row: AdminReasonRow, filter: ReasonFilter | null): boolean {
  if (filter === 'in_use') return row.retiredAt === null;
  if (filter === 'retired') return row.retiredAt !== null;
  if (filter === 'added') return row.isCustom;
  return true;
}

function matchesQuery(row: AdminReasonRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  return needle === '' || row.label.toLowerCase().includes(needle) || row.code.toLowerCase().includes(needle);
}

function usedText(times: number): string {
  return times === 0 ? 'never used' : `used ${formatNumber(times)}×`;
}
